// Cliente HTTP para a API v2 do techdash-backend.
// ADR-0001 §"D5 — Pivot" + endpoints em apps/backend/app/api/endpoints/v2/.

import { API_BASE_URL } from '../../../config/endpoints.js';
import { getAccessToken } from '../../../utils/tokenStorage.js';

async function fetchV2(path, params = {}) {
  const url = new URL(`${API_BASE_URL}/api/v2${path}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) url.searchParams.set(k, v.join(','));
      return;
    }
    url.searchParams.set(k, String(v));
  });

  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken?.();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    throw new Error(`v2 ${path} → ${res.status}`);
  }
  const data = await res.json();
  return {
    data,
    source: res.headers.get('X-Data-Source') || 'unknown',
    error: res.headers.get('X-Data-Error') === '1',
  };
}

/**
 * GET /api/v2/onus — envelope {items, total, limit, offset, has_more}.
 *
 * Parâmetros aceitos:
 *   - status:        'bad' | 'all' | 'online' | 'offline' | 'LOS' | 'dying_gasp'
 *   - search:        string livre (person/contract/onu_sn/CEP/OLT code)
 *   - olt:           string "1,3,4" (csv de olt_ids) OU array de ids
 *   - minAgingMin:   number (minutos mínimos no status atual, inclusive)
 *   - maxAgingMin:   number (minutos máximos no status atual, exclusivo).
 *                    Combinado com `minAgingMin` cria faixa [min, max).
 *                    Backend usa `COALESCE(last_change_at_olt, status_since)`.
 *   - bbox:          "lat_min,lng_min,lat_max,lng_max"
 *   - sort:          coluna (whitelist backend)
 *   - order:         'asc' | 'desc'
 *   - limit, offset: paginação
 */
export function fetchOnus({
  status = 'bad',
  search,
  olt,
  minAgingMin,
  maxAgingMin,
  bbox,
  sort,
  order,
  limit = 200,
  offset = 0,
} = {}) {
  return fetchV2('/onus', {
    status,
    search,
    olt,
    min_aging_min: minAgingMin,
    max_aging_min: maxAgingMin,
    bbox,
    sort,
    order,
    limit,
    offset,
  });
}

// GET /api/v2/onus/facets — counts agregados pra alimentar contadores nos filtros.
export function fetchOnuFacets() {
  return fetchV2('/onus/facets');
}

/**
 * GET /api/v2/snapshot — bundle completo (rows + facets) do filtro de status.
 *
 * Substitui o trio `useOnus + useOnuFacets + useClusters` no Mapa v2: o
 * frontend pede 1× a cada 30s, e todos os filtros (search/olt/aging/sort/
 * page) acontecem client-side em memória sem refetch.
 *
 * Cache backend 25s — múltiplos clients dentro da janela compartilham um
 * único hit no Postgres (header `X-Snapshot-Cache: hit|miss`).
 */
export function fetchSnapshot({ status = 'bad' } = {}) {
  return fetchV2('/snapshot', { status });
}

// GET /api/v2/onus/{sn}
export function fetchOnu(sn) {
  return fetchV2(`/onus/${encodeURIComponent(sn)}`);
}

// GET /api/v2/onus/{sn}/history?days=15
export function fetchOnuHistory(sn, days = 15) {
  return fetchV2(`/onus/${encodeURIComponent(sn)}/history`, { days });
}

// GET /api/v2/onus/{sn}/optical-history?days=7 — MAP-33
// Pontos rx/tx/distance por ciclo do poller-optical (1×/h + event-driven).
// Backend resolve SN → (olt_id, if_index, ont_index) via Redis hot snapshot;
// se Redis miss devolve [] com header X-Data-Error=redis-miss.
export function fetchOpticalHistory(sn, days = 7) {
  return fetchV2(`/onus/${encodeURIComponent(sn)}/optical-history`, { days });
}

// GET /api/v2/topology?type=
export function fetchTopology({ type = 'all', bbox, limit = 5000 } = {}) {
  return fetchV2('/topology', { type, bbox, limit });
}

/**
 * GET /api/v2/clusters — agregação geográfica server-side (MAP-20).
 *
 * Retorna [{lat, lng, count, count_bad}]. Frontend renderiza cada cluster
 * como círculo dimensionado por log(count) e colorido por count_bad/count.
 *
 * Aceita os mesmos filtros do `/onus` (status/search/olt/aging) para manter
 * o mapa sincronizado com a tabela mesmo quando o user filtra.
 */
export function fetchClusters({
  bbox,
  zoom = 11,
  status = 'bad',
  olt,
  search,
  minAgingMin,
  maxAgingMin,
} = {}) {
  return fetchV2('/clusters', {
    bbox,
    zoom,
    status,
    olt,
    search,
    min_aging_min: minAgingMin,
    max_aging_min: maxAgingMin,
  });
}
